import { useSelector } from '@/api/state';
import { getTask } from '@/utils/stateAccessors';
import MarkdownLabel from '@payaca/components/markdownLabel/MarkdownLabel';
import { ScheduledEventsPermissions } from '@payaca/permissions/scheduledEvents/scheduled-events.permissions';
import * as dealActions from '@payaca/store/deals/dealsActions';
import * as scheduledEventActions from '@payaca/store/scheduledEvents/scheduledEventsActions';
import * as taskActions from '@payaca/store/tasks/tasksActions';
import { isValidHtml, parseHtmlToReact } from '@payaca/utilities/htmlUtilities';
import { FC, useEffect, type JSX } from 'react';
import { useDispatch } from 'react-redux';
import DynamicTaskChecklistCompletionForm from '../dynamicTaskChecklistCompletionForm/DynamicTaskChecklistCompletionForm';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';
import TaskDeadlineCompletionDateInformation from '../taskDeadlineCompletionDateInformation/TaskDeadlineCompletionDateInformation';
import TaskDetailsAndActionsHeader from '../taskDetailsAndActionsHeader/TaskDetailsAndActionsHeader';
import TaskLinkedScheduledEventsPanel from '../taskLinkedScheduledEventsPanel/TaskLinkedScheduledEventsPanel';
import './TaskReadCompleteView.sass';

type Props = {
  taskId: number;
  hideDealLink?: boolean;
  onDeleteTaskSuccess?: () => void;
  onUpdateTaskSuccess?: () => void;
};
const TaskReadCompleteView: FC<Props> = ({
  taskId,
  hideDealLink,
  onDeleteTaskSuccess,
  onUpdateTaskSuccess,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();

  const task = useSelector((state) => getTask(state, taskId));

  useEffect(() => {
    dispatch(scheduledEventActions.requestGetScheduledEventsForTask(taskId));
    dispatch(taskActions.requestGetTask(taskId));
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [taskId]);

  useEffect(() => {
    if (task?.dealId) dispatch(dealActions.requestGetDeal(task.dealId));
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [task?.dealId]);

  if (!task) return null;

  return (
    <div className={'task-read-complete-view'}>
      <TaskDetailsAndActionsHeader
        taskId={taskId}
        hideDealLink={hideDealLink}
        onDeleteTaskSuccess={onDeleteTaskSuccess}
        onUpdateTaskSuccess={() => {
          dispatch(taskActions.requestGetTask(taskId));
          if (onUpdateTaskSuccess) {
            // oxlint-disable-next-line typescript/no-meaningless-void-operator -- FIXME: pre-existing error: void operator shouldn't be used on void; it should convey that a return value is being ignored
            void onUpdateTaskSuccess();
          }
        }}
      />
      <TaskDeadlineCompletionDateInformation task={task} />
      <div className="dynamic-task-checklist-completion-form-panel">
        <DynamicTaskChecklistCompletionForm
          task={task}
          onUpdateTaskCompletionSuccess={onUpdateTaskSuccess}
        />
      </div>
      {task.notes && (
        <div className="notes-container">
          <label>
            <strong>Notes:</strong>
          </label>
          {isValidHtml(task.notes) ? (
            parseHtmlToReact(task.notes)
          ) : (
            <MarkdownLabel markdown={task.notes} />
          )}
        </div>
      )}
      <PermissionGuard
        renderIfHasPermissions={[
          ScheduledEventsPermissions.GET_EVENTS,
          ScheduledEventsPermissions.GET_MY_EVENTS,
        ]}
      >
        <TaskLinkedScheduledEventsPanel taskId={taskId} />
      </PermissionGuard>
    </div>
  );
};
export default TaskReadCompleteView;
